// Compilateur — packs (données, miroir des tables Supabase) → moteur (règles compilées).
//
//   resolvePacks(allPacks, ids)     → { packs, diagnostics }          extends transitif, ordre topologique
//   compile(allPacks, ids)          → { engine, diagnostics, stats }  moteur en mémoire (UI, tests)
//   compileToSource(allPacks, ids)  → { source, diagnostics, stats }  texte d'un module ES (build)
//   loadEngine(source)              → Promise<engine>                 même objet que compile().engine
//
// Tout le travail est fait AU BUILD : les motifs sont parsés et inlinés comme littéraux AST, les gardes
// `where` deviennent de vraies fonctions JS, chaque règle reçoit un pré-filtre structurel `pre` généré.
// compile() et compileToSource() partagent la même représentation intermédiaire et le MÊME code généré
// (compile() l'évalue avec `new Function`), donc les deux moteurs se comportent à l'identique.

import { buildSyntaxTable } from './syntax-table.js';
import { parse, ParseError } from '../kernel/syntax.js';
import { equal, isLeaf } from '../kernel/ast.js';
import { buildPedagogy } from './pedagogy.js';
import { buildTables } from './tables.js';

export const COMPILER_VERSION = 1;
export const BUILTIN_NAMES = [
  'fold',
  'sqrt_factor',
  'fact_quotient',
  'fact_expand',
  'parmi_read',
  'sqrt_simplify',
  'round',
  'collect',
  'substitute',
  'formula',
  'relation_add',
  'relation_mul',
  'swap',
  'chasles',
  'vector_opposite',
  'derive_linear',
  'derive_ref',
  'limit_split',
  'limit_ops',
  'limit_growth',
  'factor_dominant',
  'unfold',
  'signs',
  'integer_bound',
  'sign_table',
  'convert',
  'name_part',
  'antiderive',
  'limit_ref',
  'limit_rewrite',
  'limit_dl',
  'limit_read',
  'limit_equiv',
  'limit_bound',
  'limit_squeeze',
  'mod_reduce',
  'geo_compute',
  'clear_denominator',
  'move_terms',
  'poly_factor',
  'zero_product',
  'zero_power',
  'root_power',
  'zero_quotient',
  'square_sides',
  'test_rank',
  'quad_discriminant',
  'quad_root',
  'quad_solutions',
  'quad_sign',
  'trig_value',
  'trig_divide',
  'trig_families',
  'trig_isolate',
  'trig_circle',
  'trig_interval',
  'angle_arccos',
  'angle_arcsin',
  'angle_arctan',
  'angle_value',
  'keep_valid',
  'aux_pose',
  'aux_back',
  'condition',
  'condition_keep',
  'cx_euler',
  'cx_trig_form',
  'cx_exp_form',
  'cx_trig_value',
  'cx_exp_rules',
  'cx_moivre',
  'cx_conjugate',
  'cx_i2',
  'cx_develop',
  'cx_arg',
  'cx_equation',
  'cx_solve',
  'cx_delta',
  'mod_fermat',
  'euclid',
  'factorize_int',
  'gcd_read',
  'sign_table_fill',
  'sign_table_read',
  'point_value',
  'use_condition',
  'tangent_equation',
];
export const RULE_KINDS = ['rewrite', 'trap', 'builtin'];
const PRE_DEPTH = 3; // profondeur du pré-filtre (racine, enfants, petits-enfants)

// ── Résolution des packs ──────────────────────────────────────────────────
function packMap(allPacks) {
  if (allPacks instanceof Map) return allPacks;
  const m = new Map();
  const list = Array.isArray(allPacks) ? allPacks : Object.values(allPacks || {});
  for (const p of list) if (p && p.id) m.set(p.id, p);
  return m;
}

export function resolvePacks(allPacks, ids) {
  const byId = packMap(allPacks);
  const diagnostics = [];
  const wanted = ids && ids.length ? ids : [...byId.keys()];
  const order = [],
    state = new Map(); // 1 = en cours, 2 = fait
  function visit(id, from, stack) {
    const st = state.get(id);
    if (st === 2) return;
    if (st === 1) {
      const cyc = [...stack.slice(stack.indexOf(id)), id].join(' → ');
      diagnostics.push({
        level: 'error',
        pack: from ?? id,
        msg: `cycle dans « extends » : ${cyc}`,
      });
      return;
    }
    const p = byId.get(id);
    if (!p) {
      diagnostics.push({
        level: 'error',
        pack: from ?? id,
        msg: from
          ? `le pack « ${from} » étend « ${id} », introuvable`
          : `pack « ${id} » introuvable`,
      });
      return;
    }
    state.set(id, 1);
    for (const dep of p.extends || []) visit(dep, id, [...stack, id]);
    state.set(id, 2);
    order.push(p);
  }
  for (const id of wanted) visit(id, null, []);
  return { packs: order, diagnostics };
}

// ── Empreinte stable (FNV-1a 32 bits ×2, sans dépendance) ─────────────────
export function stableStringify(v) {
  if (v === null || typeof v !== 'object') return JSON.stringify(v);
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(',')}]`;
  return `{${Object.keys(v)
    .sort()
    .filter((k) => v[k] !== undefined)
    .map((k) => `${JSON.stringify(k)}:${stableStringify(v[k])}`)
    .join(',')}}`;
}
export function fnv1a(str, seed = 0x811c9dc5) {
  let h = seed >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}
function hashPacks(packs) {
  const s = stableStringify({ compiler: COMPILER_VERSION, packs });
  return fnv1a(s) + fnv1a(s, 0x01000193 ^ 0x9e3779b9);
}

// ── Forme normale légère des motifs (aplatissement assoc, Neg(Num) → Num, double négation) ──
export function lightPattern(n, table) {
  const negType = negTypeOf(table);
  const go = (x) => {
    if (isLeaf(x)) return x;
    let k = x.k.map(go);
    const spec = table.nodes[x.t];
    if (spec?.props?.includes('assoc'))
      k = k.flatMap((c) => (c.t === x.t && c.name === x.name ? c.k : [c]));
    if (x.t === negType && k.length === 1) {
      const c = k[0];
      if (c.t === 'Num') return { t: 'Num', p: c.p === 0 ? 0 : -c.p, q: c.q };
      if (c.t === negType) return c.k[0];
    }
    return { ...x, k };
  };
  return go(n);
}
function negTypeOf(table) {
  return Object.keys(table.nodes).find((t) => table.nodes[t].eval === 'neg') || 'Neg';
}
function varsOf(n, out = []) {
  if (n.t === 'Var') {
    if (!out.includes(n.name)) out.push(n.name);
  } else if (!isLeaf(n)) n.k.forEach((c) => varsOf(c, out));
  return out;
}
function typesOf(n, out = new Set()) {
  if (!isLeaf(n)) {
    out.add(n.t);
    n.k.forEach((c) => typesOf(c, out));
  }
  return out;
}

// ── Pré-filtre structurel généré ──────────────────────────────────────────
// Doit être CORRECT : ne jamais rejeter un nœud que le motif pourrait filtrer (CONTRACT §6).
// Prudences : nœud assoc+comm → arité MINIMALE et enfants exigés cherchés avec some() ; Neg et
// nombres confondus (la forme légère transforme Neg(Num) en Num), Neg(?var) n'exige rien (double
// négation), un motif produit accepte aussi un Neg (vu comme Mul[−1, …]) ; profondeur limitée.
const J = JSON.stringify;
function preCond(p, X, table, depth) {
  const negType = negTypeOf(table);
  switch (p.t) {
    case 'Var':
      return null;
    case 'Num':
      return p.p < 0
        ? `((${X}.t === "Num" && ${X}.p === ${p.p} && ${X}.q === ${p.q}) || ${X}.t === ${J(negType)})`
        : `(${X}.t === "Num" && ${X}.p === ${p.p} && ${X}.q === ${p.q})`;
    case 'Sym':
      return `(${X}.t === "Sym" && ${X}.name === ${J(p.name)})`;
  }
  if (p.t === negType) {
    // −?b peut filtrer N'IMPORTE QUOI : avec ?b = −y, la forme légère donne y (double négation),
    // avec ?b = −4 elle donne 4. Sur un composé, on garde « Neg ou nombre ».
    if (p.k[0].t === 'Var') return null;
    return `(${X}.t === ${J(negType)} || ${X}.t === "Num")`;
  }
  const spec = table.nodes[p.t] || {};
  const ac = spec.props?.includes('assoc') && spec.props?.includes('comm');
  const parts = [`${X}.t === ${J(p.t)}`];
  if (p.name !== undefined) parts.push(`${X}.name === ${J(p.name)}`);
  if (depth >= PRE_DEPTH) return parts.length > 1 ? `(${parts.join(' && ')})` : parts[0];
  if (ac) {
    parts.push(`${X}.k.length >= ${p.k.length}`);
    const c = `c${depth}`;
    for (const child of p.k) {
      const sub = preCond(child, c, table, depth + 1);
      if (sub) parts.push(`${X}.k.some((${c}) => ${sub})`);
    }
  } else {
    parts.push(`${X}.k.length === ${p.k.length}`);
    p.k.forEach((child, i) => {
      const sub = preCond(child, `${X}.k[${i}]`, table, depth + 1);
      if (sub) parts.push(sub);
    });
  }
  const cond = parts.length > 1 ? `(${parts.join(' && ')})` : parts[0];
  // un motif produit filtre aussi un opposé de produit, vu comme Mul[−1, …] (forme légère du juge)
  return spec.eval === 'mul' ? `(${cond} || ${X}.t === ${J(negType)})` : cond;
}
function preSource(lhs, table) {
  const c = preCond(lhs, 'n', table, 0);
  if (!c) return '(n) => true';
  return `(n) => ${c.startsWith('(') && c.endsWith(')') && balancedOuter(c) ? c.slice(1, -1) : c}`;
}
function balancedOuter(s) {
  let d = 0;
  for (let i = 0; i < s.length; i++) {
    if (s[i] === '(') d++;
    else if (s[i] === ')') {
      d--;
      if (d === 0 && i < s.length - 1) return false;
    }
  }
  return true;
}

// ── Mini-parseur des gardes `where` ───────────────────────────────────────
// garde := ou ; ou := et ('||' et)* ; et := non ('&&' non)* ; non := '!' non | atome
// atome := '(' garde ')' | prédicat '(' arg ((',' | ';') arg)* ')' ; arg := ?var | symbole | nombre
export class GuardError extends Error {}
export function compileGuard(src, { predicates, vars }) {
  const toks = [];
  const re =
    /\s*(?:(\|\||&&|[!(),;])|\?([A-Za-z][A-Za-z0-9_]*)|(-?[0-9]+(?:[.,][0-9]+)?)|([^\s!(),;|&?]+))/y;
  let m,
    pos = 0;
  while (pos < src.length) {
    re.lastIndex = pos;
    m = re.exec(src);
    if (!m || m[0].length === 0) {
      if (/^\s*$/.test(src.slice(pos))) break;
      throw new GuardError(`garde illisible à partir de « ${src.slice(pos).trim()} »`);
    }
    pos = re.lastIndex;
    if (m[1]) toks.push({ k: 'op', v: m[1] });
    else if (m[2]) toks.push({ k: 'var', v: m[2] });
    else if (m[3]) toks.push({ k: 'num', v: m[3] });
    else if (m[4]) toks.push({ k: 'id', v: m[4] });
  }
  let i = 0;
  const peek = () => toks[i];
  const isOp = (v) => peek()?.k === 'op' && peek().v === v;
  const eat = (v) => {
    if (!isOp(v)) throw new GuardError(`« ${v} » attendu dans la garde`);
    i++;
  };
  const used = [];
  function or() {
    let s = and();
    while (isOp('||')) {
      i++;
      s = `${s} || ${and()}`;
    }
    return s;
  }
  function and() {
    let s = not();
    while (isOp('&&')) {
      i++;
      s = `${s} && ${not()}`;
    }
    return s;
  }
  function not() {
    if (isOp('!')) {
      i++;
      return `!${not()}`;
    }
    return atom();
  }
  function atom() {
    const t = peek();
    if (!t) throw new GuardError('la garde s’arrête trop tôt');
    if (isOp('(')) {
      i++;
      const s = or();
      eat(')');
      return `(${s})`;
    }
    if (t.k !== 'id') throw new GuardError(`prédicat attendu, trouvé « ${t.v} »`);
    i++;
    if (!predicates.includes(t.v))
      throw new GuardError(`prédicat inconnu « ${t.v} » (connus : ${predicates.join(', ')})`);
    eat('(');
    const args = [];
    if (!isOp(')')) {
      args.push(arg());
      while (isOp(',') || isOp(';')) {
        i++;
        args.push(arg());
      }
    }
    eat(')');
    used.push(t.v);
    return `K.pred.${t.v}(${args.join(', ')})`;
  }
  function arg() {
    const t = toks[i++];
    if (!t) throw new GuardError('argument manquant dans la garde');
    if (t.k === 'var') {
      if (!vars.includes(t.v)) throw new GuardError(`la garde utilise ?${t.v}, absente du motif`);
      return `b.${t.v}`;
    }
    if (t.k === 'num') return String(Number(t.v.replace(',', '.')));
    if (t.k === 'id') return J(t.v);
    throw new GuardError(`argument inattendu « ${t.v} »`);
  }
  const body = or();
  if (i < toks.length) throw new GuardError(`garde : suite inattendue « ${toks[i].v} »`);
  return { source: `(b, K) => ${body}`, predicates: used };
}

// ── Représentation intermédiaire ──────────────────────────────────────────
function build(allPacks, ids) {
  const t0 = now();
  const resolved = resolvePacks(allPacks, ids);
  const packs = resolved.packs;
  const diagnostics = [...resolved.diagnostics];
  const { table, diagnostics: synDiag } = buildSyntaxTable(packs);
  diagnostics.push(...synDiag);
  const predicates = [...new Set(packs.flatMap((p) => p.predicates || []))];

  const err = (pack, rule, msg) => diagnostics.push({ level: 'error', pack, rule, msg });
  const warn = (pack, rule, msg) => diagnostics.push({ level: 'warning', pack, rule, msg });

  const rules = []; // IR : { ...meta, lhs, rhs, vars, guardSrc, preSrc, src }
  const builtinDefs = {}; // nom → dernière définition (libellés hérités par les activations)
  // niveaux (CONTRACT §11) : lus avant les règles, pour valider implicitFrom
  const levels = buildLevels(packs, diagnostics);
  const checkImplicit = (v, pack, rid) => {
    if (v === undefined || v === null) return null;
    if (!levels) {
      err(pack, rid, `« implicitFrom » (${v}) sans « levels » déclarés dans les packs`);
      return null;
    }
    if (!levels.order.includes(v)) {
      err(pack, rid, `« implicitFrom » : niveau « ${v} » inconnu (${levels.order.join(', ')})`);
      return null;
    }
    return v;
  };
  const builtinActive = {}; // nom → IR de la règle active
  const seenIds = new Set();

  const parsePattern = (pack, rid, text, side) => {
    if (typeof text !== 'string' || !text.trim()) {
      err(pack.id, rid, `motif ${side} manquant`);
      return null;
    }
    try {
      return lightPattern(parse(text, table, { patternVars: true }), table);
    } catch (e) {
      if (e instanceof ParseError)
        err(pack.id, rid, `motif ${side} « ${text} » illisible : ${e.message} (position ${e.pos})`);
      else err(pack.id, rid, `motif ${side} « ${text} » illisible : ${e.message}`);
      return null;
    }
  };

  for (const pack of packs) {
    for (const r of pack.rules || []) {
      const version = r.version ?? 1;
      const rid = `${pack.id}/${r.key}@${version}`;
      if (!r.key) {
        err(pack.id, rid, 'règle sans « key »');
        continue;
      }
      if (seenIds.has(rid)) {
        err(pack.id, rid, `règle « ${rid} » définie deux fois`);
        continue;
      }
      seenIds.add(rid);
      if (!RULE_KINDS.includes(r.kind)) {
        err(pack.id, rid, `genre de règle inconnu « ${r.kind} » (rewrite, trap ou builtin)`);
        continue;
      }

      const base = { id: rid, pack: pack.id, key: r.key, version, kind: r.kind };
      if (r.title !== undefined) base.title = r.title;
      if (r.misconception_id !== undefined) base.misconception_id = r.misconception_id;
      if (r.card !== undefined) base.card = r.card;
      // le geste comprend le calcul de son résultat (« 1/3 + 1/3 → 2/3 » en une fois) : le juge propose aussi le
      // résultat calculé, dans le même geste
      if (r.withCalc === true) base.withCalc = true;
      // gestes d'une même famille (« develop ») : plusieurs dans une ligne comptent pour un seul
      if (typeof r.gesture === 'string' && /^[a-z][a-z0-9_-]*$/.test(r.gesture))
        base.gesture = r.gesture;
      else if (r.gesture !== undefined) err(pack.id, rid, `gesture « ${r.gesture} » invalide`);

      if (r.kind === 'builtin') {
        if (!BUILTIN_NAMES.includes(r.builtin)) {
          err(
            pack.id,
            rid,
            `builtin « ${r.builtin} » inconnu du noyau (connus : ${BUILTIN_NAMES.join(', ')})`,
          );
          continue;
        }
        const prev = builtinDefs[r.builtin] || {};
        const label = r.label ?? prev.label,
          why = r.why ?? prev.why,
          hora = r.hora ?? prev.hora;
        if (!checkHora(hora, pack.id, rid, err)) continue;
        if (!label)
          err(
            pack.id,
            rid,
            `builtin « ${r.builtin} » sans libellé (et aucun pack étendu ne le définit)`,
          );
        if (!why) warn(pack.id, rid, `builtin « ${r.builtin} » sans « why »`);
        const implicitFrom = checkImplicit(
          r.implicitFrom !== undefined ? r.implicitFrom : prev.implicitFrom,
          pack.id,
          rid,
        );
        // réglages du geste (données, lus par la stratégie du noyau) ; une clé « …From » est un niveau connu
        const options = r.options ?? prev.options;
        for (const [k, v] of Object.entries(options ?? {}))
          if (/From$/.test(k)) checkImplicit(v, pack.id, `${rid}:${k}`);
        const ir = {
          ...base,
          label: label ?? r.builtin,
          why: why ?? '',
          hora,
          builtin: r.builtin,
          implicitFrom,
          options,
          lhs: null,
          rhs: null,
          vars: [],
          free: [],
          guardSrc: null,
          preSrc: '(n) => true',
          src: {},
        };
        builtinDefs[r.builtin] = { label: ir.label, why: ir.why, hora, implicitFrom, options };
        if (r.enabled === false) continue; // catalogue seulement : libellé / pourquoi / HORA hérités
        if (builtinActive[r.builtin] && builtinActive[r.builtin].pack !== pack.id) {
          // une activation dans un pack plus spécialisé remplace la précédente
          const old = builtinActive[r.builtin];
          rules.splice(rules.indexOf(old), 1);
        } else if (builtinActive[r.builtin]) {
          warn(pack.id, rid, `builtin « ${r.builtin} » activé deux fois dans le même pack`);
        }
        builtinActive[r.builtin] = ir;
        rules.push(ir);
        continue;
      }

      // rewrite / trap
      const hora = r.hora ?? (r.kind === 'trap' ? 0 : undefined);
      if (!checkHora(hora, pack.id, rid, err)) continue;
      if (!r.label) warn(pack.id, rid, 'règle sans libellé');
      if (!r.why) warn(pack.id, rid, 'règle sans « why »');
      if (r.kind === 'trap' && !r.title) warn(pack.id, rid, 'piège sans « title »');
      if (r.kind === 'trap' && r.bidirectional)
        warn(pack.id, rid, 'un piège ne peut pas être bidirectionnel : sens inverse ignoré');
      const lhs = parsePattern(pack, rid, r.lhs, 'lhs');
      const rhs = parsePattern(pack, rid, r.rhs, 'rhs');
      if (!lhs || !rhs) continue;

      // variables libres : présentes dans le rhs, absentes du lhs, liées par le juge depuis la ligne visée
      const freeList = (x, field) => {
        if (x == null) return [];
        if (
          !Array.isArray(x) ||
          x.some((v) => typeof v !== 'string' || !/^\??[A-Za-z][A-Za-z0-9_]*$/.test(v))
        ) {
          err(pack.id, rid, `« ${field} » doit être une liste de noms de variables (ex. ["k"])`);
          return [];
        }
        return [...new Set(x.map((v) => v.replace(/^\?/, '')))];
      };
      const mk = (id, L, R, extra, declaredFree) => {
        for (const t of [...typesOf(L), ...typesOf(R)])
          if (!table.nodes[t]) err(pack.id, id, `nœud inconnu « ${t} » dans le motif`);
        const vars = varsOf(L),
          rvars = varsOf(R);
        const free = [];
        for (const v of declaredFree) {
          if (vars.includes(v))
            err(pack.id, id, `la variable libre ?${v} apparaît aussi dans le lhs`);
          else if (!rvars.includes(v))
            err(pack.id, id, `la variable libre ?${v} est déclarée mais absente du rhs`);
          else free.push(v);
        }
        for (const v of rvars)
          if (!vars.includes(v) && !declaredFree.includes(v)) {
            err(
              pack.id,
              id,
              `la variable ?${v} du rhs n’apparaît pas dans le lhs${extra.rev ? ' (sens inverse : déclare-la dans « freeRev »)' : ' (déclare-la dans « free » si elle est libre)'}`,
            );
          }
        if (equal(L, R)) err(pack.id, id, 'la règle boucle : rhs identique au lhs');
        let guardSrc = null;
        if (r.where != null && String(r.where).trim()) {
          try {
            guardSrc = compileGuard(String(r.where), {
              predicates,
              vars: [...vars, ...free],
            }).source;
          } catch (e) {
            err(pack.id, id, `garde « ${r.where} » : ${e.message}`);
            guardSrc = '(b, K) => false';
          }
        }
        return {
          ...base,
          ...extra,
          id,
          lhs: L,
          rhs: R,
          vars,
          free,
          guardSrc,
          preSrc: preSource(L, table),
          src: { lhs: extra.rev ? r.rhs : r.lhs, rhs: extra.rev ? r.lhs : r.rhs, where: r.where },
        };
      };
      const free = freeList(r.free, 'free'),
        freeRev = freeList(r.freeRev, 'freeRev');
      const implicitFrom = checkImplicit(r.implicitFrom, pack.id, rid);
      const implicitFromRev =
        r.implicitFromRev !== undefined
          ? checkImplicit(r.implicitFromRev, pack.id, rid + ':rev')
          : implicitFrom;
      rules.push(
        mk(rid, lhs, rhs, { label: r.label ?? r.key, why: r.why ?? '', hora, implicitFrom }, free),
      );
      if (r.bidirectional && r.kind === 'rewrite') {
        const horaRev = r.horaRev ?? hora;
        if (checkHora(horaRev, pack.id, rid + ':rev', err)) {
          rules.push(
            mk(
              rid + ':rev',
              rhs,
              lhs,
              {
                label: r.labelRev ?? r.label ?? r.key,
                why: r.whyRev ?? r.why ?? '',
                hora: horaRev,
                rev: true,
                implicitFrom: implicitFromRev,
                gesture: undefined, // le sens inverse (factoriser) n'est pas de la famille
              },
              freeRev,
            ),
          );
        }
      }
    }
  }

  // doublons et paires qui bouclent (rewrite/trap)
  const rw = rules.filter((r) => r.kind !== 'builtin');
  for (let i = 0; i < rw.length; i++) {
    for (let j = i + 1; j < rw.length; j++) {
      const a = rw[i],
        b = rw[j];
      if (
        a.kind === b.kind &&
        equal(a.lhs, b.lhs) &&
        equal(a.rhs, b.rhs) &&
        !a.guardSrc &&
        !b.guardSrc
      ) {
        warn(b.pack, b.id, `doublon de ${a.id} (mêmes motifs)`);
      } else if (
        a.kind === 'rewrite' &&
        b.kind === 'rewrite' &&
        a.pack === b.pack &&
        !a.rev &&
        !b.rev &&
        equal(a.lhs, b.rhs) &&
        equal(a.rhs, b.lhs) &&
        a.id.split(':')[0] !== b.id.split(':')[0]
      ) {
        diagnostics.push({
          level: 'error',
          pack: b.pack,
          rule: b.id,
          msg: `la règle boucle avec ${a.id} (paire inverse sans orientation) : garde une seule règle avec « bidirectional: true »`,
        });
      }
    }
  }

  // index par type de racine du lhs
  const index = { '*': [] };
  const negType = negTypeOf(table);
  rules.forEach((r, i) => {
    if (r.kind === 'builtin') return;
    const t = r.lhs.t === 'Var' || (r.lhs.t === negType && r.lhs.k[0].t === 'Var') ? '*' : r.lhs.t; // −?a filtre tout
    (index[t] ||= []).push(i);
    if (t === negType) (index.Num ||= []).push(i); // Neg(Num) devient un Num négatif : prudence
    if (table.nodes[t]?.eval === 'mul') (index[negType] ||= []).push(i); // −(a × b) vu comme Mul[−1, a, b]
  });
  const builtins = {};
  rules.forEach((r, i) => {
    if (r.kind === 'builtin') builtins[r.builtin] = i;
  });

  // pédagogie (CONTRACT §9) : sondes, conseils, outils, clavier
  const peda = buildPedagogy(packs, rules, table);
  diagnostics.push(...peda.diagnostics);
  // tableaux (lots F4 / F5) : pièges, messages et touches des cases
  const tabs = buildTables(packs);
  diagnostics.push(...tabs.diagnostics);
  const quantities = buildQuantities(packs, diagnostics);
  const geometry = buildGeometry(packs, diagnostics);

  const hash = hashPacks(packs);
  const errors = diagnostics.filter((d) => d.level === 'error').length;
  const stats = {
    packs: packs.length,
    rules: rules.length,
    rewrite: rules.filter((r) => r.kind === 'rewrite').length,
    trap: rules.filter((r) => r.kind === 'trap').length,
    builtin: rules.filter((r) => r.kind === 'builtin').length,
    advice: peda.advice.length,
    tools: peda.tools.length,
    errors,
    warnings: diagnostics.length - errors,
    ms: Math.round((now() - t0) * 100) / 100,
  };
  const meta = {
    packs: packs.map((p) => ({ id: p.id, version: p.version ?? 1 })),
    hash,
    builtAt: new Date().toISOString(),
    stats,
  };
  return {
    meta,
    table,
    rules,
    index,
    builtins,
    diagnostics,
    stats,
    probes: peda.probes,
    advice: peda.advice,
    tools: peda.tools,
    keyboard: peda.keyboard,
    display: peda.display,
    tables: tabs.tables,
    levels: levels ? { order: levels.order, profiles: levels.profiles } : null,
    quantities,
    geometry,
  };
}

// ── Grandeurs et unités (lot M5) ──────────────────────────────────────────
// « units » : [{ name, dim: { L: 1, … }, factor }] (facteur vers l'unité de base de la dimension), « dimensions » :
// [{ dim, name }] (nom d'une nature, pour les messages). Réunis de tous les packs ; null s'il n'y en a aucun.
function buildQuantities(packs, diagnostics) {
  const units = {},
    dimensions = [];
  for (const pack of packs) {
    const e = (msg) => diagnostics.push({ level: 'error', pack: pack.id, msg });
    for (const u of pack.units ?? []) {
      if (!u || typeof u.name !== 'string' || !u.name.trim() || /\s/.test(u.name)) {
        e('unité sans nom (ou nom avec une espace)');
        continue;
      }
      if (typeof u.factor !== 'number' || !(u.factor > 0)) {
        e(`unité « ${u.name} » : facteur positif attendu`);
        continue;
      }
      if (
        !u.dim ||
        typeof u.dim !== 'object' ||
        Object.values(u.dim).some((x) => !Number.isInteger(x))
      ) {
        e(`unité « ${u.name} » : dimension { nom: exposant entier } attendue`);
        continue;
      }
      units[u.name] = { dim: u.dim, factor: u.factor };
    }
    for (const d of pack.dimensions ?? []) {
      if (!d || typeof d.name !== 'string' || !d.dim || typeof d.dim !== 'object') {
        e('dimension sans nom ou sans « dim »');
        continue;
      }
      dimensions.push({ dim: d.dim, name: d.name });
    }
  }
  return Object.keys(units).length ? { units, dimensions } : null;
}

// ── Géométrie déductive (lot I6) ────────────────────────────────────────────
// « geometry » : { hora, rules: [{ id, label, card, if: [motifs], then: [motifs], levels, when? }] } — règles
// déclaratives (« ?I milieu de [?A?B] »…), réunies de tous les packs ; null s'il n'y en a aucune.
function buildGeometry(packs, diagnostics) {
  const rules = [];
  for (const pack of packs) {
    const G = pack.geometry;
    if (!G) continue;
    const e = (msg) => diagnostics.push({ level: 'error', pack: pack.id, msg });
    for (const r of G.rules ?? []) {
      const strs = (x) =>
        Array.isArray(x) && x.length > 0 && x.every((y) => typeof y === 'string' && y.trim());
      if (!r || typeof r.id !== 'string' || !r.label || !strs(r.if) || !strs(r.then)) {
        e(`règle de géométrie « ${r?.id ?? '?'} » : id, label, if et then (motifs) attendus`);
        continue;
      }
      rules.push({
        ...r,
        id: `${pack.id}/${r.id}@${pack.version ?? 1}`,
        key: r.id,
        hora: r.hora ?? G.hora ?? 2,
      });
    }
  }
  return rules.length ? { rules } : null;
}

// ── Niveaux (CONTRACT §11) ────────────────────────────────────────────────
// Le dernier pack qui déclare « levels » l'emporte (normalement core). Profils : le premier qui contient le niveau.
function buildLevels(packs, diagnostics) {
  let out = null;
  for (const pack of packs) {
    const L = pack.levels;
    if (L == null) continue;
    const e = (msg) => diagnostics.push({ level: 'error', pack: pack.id, msg });
    const before = diagnostics.length;
    if (typeof L !== 'object' || Array.isArray(L)) {
      e('« levels » doit être un objet { order, profiles }');
      continue;
    }
    const order = L.order;
    if (
      !Array.isArray(order) ||
      !order.length ||
      order.some((x) => typeof x !== 'string' || !x.trim())
    ) {
      e('levels.order doit être une liste non vide de niveaux (ex. ["6e", "5e", …])');
      continue;
    }
    const dup = order.filter((x, i) => order.indexOf(x) !== i);
    if (dup.length) e(`levels.order : niveau en double « ${[...new Set(dup)].join(', ')} »`);
    const profiles = [];
    if (!Array.isArray(L.profiles) || !L.profiles.length)
      e('levels.profiles doit être une liste non vide');
    for (const [i, p] of (Array.isArray(L.profiles) ? L.profiles : []).entries()) {
      const n = `levels.profiles[${i}]`;
      const a = order.indexOf(p?.from),
        b = order.indexOf(p?.to);
      let ok = true;
      if (a < 0) {
        e(`${n} : niveau de départ « ${p?.from} » absent de levels.order`);
        ok = false;
      }
      if (b < 0) {
        e(`${n} : niveau d’arrivée « ${p?.to} » absent de levels.order`);
        ok = false;
      }
      if (ok && b < a) {
        e(`${n} : intervalle vide (« ${p.from} » vient après « ${p.to} »)`);
        ok = false;
      }
      if (!Number.isInteger(p?.maxGestures) || p.maxGestures < 1 || p.maxGestures > 4) {
        e(`${n} : maxGestures ${p?.maxGestures} hors de 1..4`);
        ok = false;
      }
      for (const f of Object.keys(p || {}))
        if (!['from', 'to', 'maxGestures'].includes(f)) {
          diagnostics.push({
            level: 'warning',
            pack: pack.id,
            msg: `${n} : champ « ${f} » ignoré`,
          });
        }
      if (ok) profiles.push({ from: p.from, to: p.to, maxGestures: p.maxGestures });
    }
    const uncovered = order.filter((lv) => {
      const x = order.indexOf(lv);
      return !profiles.some((p) => order.indexOf(p.from) <= x && x <= order.indexOf(p.to));
    });
    if (uncovered.length) e(`levels.profiles ne couvre pas : ${uncovered.join(', ')}`);
    profiles.forEach((p, i) => {
      const a = order.indexOf(p.from),
        b = order.indexOf(p.to);
      const shadowed = order
        .slice(a, b + 1)
        .every((lv) =>
          profiles
            .slice(0, i)
            .some(
              (q) =>
                order.indexOf(q.from) <= order.indexOf(lv) &&
                order.indexOf(lv) <= order.indexOf(q.to),
            ),
        );
      if (shadowed)
        diagnostics.push({
          level: 'warning',
          pack: pack.id,
          msg: `levels.profiles[${i}] (${p.from} → ${p.to}) n’est jamais utilisé : les profils précédents couvrent déjà ces niveaux`,
        });
    });
    if (diagnostics.slice(before).some((d) => d.level === 'error')) continue;
    if (out)
      diagnostics.push({
        level: 'warning',
        pack: pack.id,
        msg: `« levels » remplace celui du pack ${out.pack}`,
      });
    out = { order: [...order], profiles, pack: pack.id };
  }
  return out;
}

function checkHora(h, pack, rid, err) {
  if (h === undefined || h === null) {
    err(pack, rid, 'HORA manquant');
    return false;
  }
  if (!Number.isInteger(h) || h < 0 || h > 12) {
    err(pack, rid, `HORA ${h} hors de 0..12`);
    return false;
  }
  return true;
}
const now = () => (globalThis.performance?.now ? performance.now() : Date.now());

// ── Matérialisation en mémoire ────────────────────────────────────────────
const fnCache = new Map();
function makeFn(src) {
  if (src == null) return null;
  let f = fnCache.get(src);
  if (!f) {
    f = new Function(`return (${src});`)();
    fnCache.set(src, f);
  }
  return f;
}
const META_KEYS = [
  'id',
  'pack',
  'key',
  'version',
  'kind',
  'label',
  'why',
  'hora',
  'title',
  'misconception_id',
  'card',
  'builtin',
  'rev',
  'implicitFrom',
  'options',
  'withCalc',
  'gesture',
];
function ruleMeta(r) {
  const o = {};
  for (const k of META_KEYS) if (r[k] !== undefined) o[k] = r[k];
  return o;
}

export function compile(allPacks, ids) {
  const ir = build(allPacks, ids);
  const rules = ir.rules.map((r) => ({
    ...ruleMeta(r),
    implicitFrom: r.implicitFrom ?? null,
    lhs: r.lhs,
    rhs: r.rhs,
    vars: r.vars,
    free: r.free,
    guard: makeFn(r.guardSrc),
    pre: makeFn(r.preSrc),
    src: r.src,
  }));
  const builtins = {};
  for (const [name, i] of Object.entries(ir.builtins)) builtins[name] = rules[i];
  const advice = ir.advice.map(({ whenSrc, ...a }) => ({ ...a, when: makeFn(whenSrc) }));
  const engine = {
    meta: ir.meta,
    table: ir.table,
    rules,
    index: ir.index,
    builtins,
    probes: ir.probes,
    advice,
    tools: ir.tools,
    keyboard: ir.keyboard,
    display: ir.display,
    tables: ir.tables,
    levels: ir.levels,
    quantities: ir.quantities,
    geometry: ir.geometry,
  };
  return { engine, diagnostics: ir.diagnostics, stats: ir.stats };
}

// ── Source générée ────────────────────────────────────────────────────────
export function compileToSource(allPacks, ids) {
  const ir = build(allPacks, ids);
  const L = [];
  L.push('// ════════════════════════════════════════════════════════════════════════');
  L.push('// Moteur JeRaisonne — GÉNÉRÉ par src/compiler/compile.js. NE PAS ÉDITER :');
  L.push('// modifiez les packs (packs/*.json, futures tables Supabase) puis relancez le build.');
  L.push('// ════════════════════════════════════════════════════════════════════════');
  L.push(`// Packs : ${ir.meta.packs.map((p) => `${p.id}@${p.version}`).join(', ')}`);
  L.push(
    `// Empreinte : ${ir.meta.hash}   Compilateur v${COMPILER_VERSION}   Construit le ${ir.meta.builtAt}`,
  );
  L.push(
    `// Règles : ${ir.stats.rules} (${ir.stats.rewrite} rewrite, ${ir.stats.trap} trap, ${ir.stats.builtin} builtin)`,
  );
  if (ir.stats.errors)
    L.push(`// ⚠ ${ir.stats.errors} ERREUR(S) DE BUILD : moteur partiel (voir meta.diagnostics).`);
  L.push('');
  L.push(`const meta = ${JSON.stringify({ ...ir.meta, diagnostics: ir.diagnostics })};`);
  L.push('');
  L.push('// ── Table de syntaxe (lexique, opérateurs, nœuds) ──');
  L.push(`const table = ${JSON.stringify(ir.table)};`);
  L.push('');
  L.push('// ── Règles ──');
  L.push('const rules = [');
  for (const r of ir.rules) {
    L.push(
      `  // ${r.id} — ${oneLine(r.label)}${r.kind !== 'rewrite' ? ` [${r.kind}${r.builtin ? ' ' + r.builtin : ''}]` : ''}`,
    );
    if (r.src.lhs)
      L.push(
        `  //   ${oneLine(r.src.lhs)}  →  ${oneLine(r.src.rhs)}${r.free.length ? `   (libres : ${r.free.map((v) => '?' + v).join(', ')})` : ''}${r.src.where ? `   si ${oneLine(r.src.where)}` : ''}`,
      );
    L.push('  {');
    for (const [k, v] of Object.entries({ ...ruleMeta(r), implicitFrom: r.implicitFrom ?? null }))
      L.push(`    ${k}: ${JSON.stringify(v)},`);
    L.push(`    lhs: ${JSON.stringify(r.lhs)},`);
    L.push(`    rhs: ${JSON.stringify(r.rhs)},`);
    L.push(`    vars: ${JSON.stringify(r.vars)},`);
    L.push(`    free: ${JSON.stringify(r.free)},`);
    L.push(`    guard: ${r.guardSrc ?? 'null'},`);
    L.push(`    pre: ${r.preSrc},`);
    L.push(`    src: ${JSON.stringify(r.src)},`);
    L.push('  },');
  }
  L.push('];');
  L.push('');
  L.push(
    '// ── Index : règles rewrite/trap par type de racine du lhs (« * » : racine variable) ──',
  );
  L.push(`const index = ${JSON.stringify(ir.index)};`);
  L.push('');
  L.push('// ── Gestes portés par le noyau, activés par les packs ──');
  L.push(
    `const builtins = { ${Object.entries(ir.builtins)
      .map(([n, i]) => `${JSON.stringify(n)}: rules[${i}]`)
      .join(', ')} };`,
  );
  L.push('');
  L.push('// ── Pédagogie (CONTRACT §9) : sondes du noyau, conseils, outils à choix, clavier ──');
  L.push(`const probes = ${JSON.stringify(ir.probes)};`);
  L.push('');
  L.push('const advice = [');
  for (const a of ir.advice) {
    L.push(
      `  // ${a.id} — sonde ${a.probe}${a.src.when ? `, si ${oneLine(a.src.when)}` : ''} ; sur ${a.on.join(', ')}`,
    );
    L.push('  {');
    for (const k of ['id', 'pack', 'key', 'version', 'on', 'after', 'probe'])
      L.push(`    ${k}: ${JSON.stringify(a[k])},`);
    L.push(`    when: ${a.whenSrc},`);
    L.push(`    message: ${JSON.stringify(a.message)},`);
    L.push(`    fields: ${JSON.stringify(a.fields)},`);
    L.push(`    src: ${JSON.stringify(a.src)},`);
    L.push('  },');
  }
  L.push('];');
  L.push('');
  L.push(`const tools = ${JSON.stringify(ir.tools, null, 2)};`);
  L.push('');
  L.push(`const keyboard = ${JSON.stringify(ir.keyboard)};`);
  L.push(`const display = ${JSON.stringify(ir.display)};`);
  L.push(`const tables = ${JSON.stringify(ir.tables)};`);
  L.push('');
  L.push('// ── Niveaux (CONTRACT §11) : nombre de gestes non implicites admis par niveau ──');
  L.push(`const levels = ${JSON.stringify(ir.levels)};`);
  L.push(`const quantities = ${JSON.stringify(ir.quantities)};`);
  L.push(`const geometry = ${JSON.stringify(ir.geometry)};`);
  L.push('');
  L.push(
    'const engine = { meta: { packs: meta.packs, hash: meta.hash, builtAt: meta.builtAt, stats: meta.stats }, table, rules, index, builtins, probes, advice, tools, keyboard, display, tables, levels, quantities, geometry };',
  );
  L.push('export const diagnostics = meta.diagnostics;');
  L.push('export default engine;');
  L.push('');
  const source = L.join('\n');
  const stats = { ...ir.stats, bytes: utf8Length(source) };
  return { source, diagnostics: ir.diagnostics, stats, hash: ir.meta.hash, meta: ir.meta };
}

function oneLine(s) {
  return String(s ?? '')
    .replace(/[\r\n\u2028\u2029]+/g, ' ')
    .replace(/\*\//g, '*∕');
}
function utf8Length(s) {
  return typeof TextEncoder !== 'undefined' ? new TextEncoder().encode(s).length : s.length;
}

// ── Chargement d'une source générée (Node ET navigateur, même code) ───────
export async function loadEngine(source) {
  const url = 'data:text/javascript;charset=utf-8,' + encodeURIComponent(source);
  const mod = await import(/* @vite-ignore */ url);
  return mod.default;
}
